import './Search.css';
import UploadDocument from '../Upload/Upload.js';
import SERVER_URL from '../../../../../env.js';
import { useEffect, useState, useMemo, useCallback } from 'react';
import Cookies from 'js-cookie';

function Search({ currentClientID, currentClientName }) {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Search filters
    const [searchYear, setSearchYear] = useState('');
    const [searchName, setSearchName] = useState('');
    const [searchType, setSearchType] = useState('');

    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [editName, setEditName] = useState('');
    const [editYear, setEditYear] = useState('');
    const [editType, setEditType] = useState('');
    const [saving, setSaving] = useState(false);
    const [editError, setEditError] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    // Upload modal state
    const [uploadModalOpen, setUploadModalOpen] = useState(false);

    const openUploadModal = () => {
        setUploadModalOpen(true);
    };

    const closeUploadModal = () => {
        setUploadModalOpen(false);
    };

    const [downloadingId, setDownloadingId] = useState(null);

    const fetchDocuments = useCallback(() => {
        if (!currentClientID) return;
        setLoading(true);
        setError('');
        fetch(`${SERVER_URL}/documents/client/${currentClientID}`, {
            headers: { 'Authorization': 'Bearer ' + Cookies.get('accessToken') },
        })
            .then(async (res) => {
                if (!res.ok) {
                    const d = await res.json().catch(() => ({}));
                    throw new Error(d.error || `HTTP ${res.status}`);
                }
                return res.json();
            })
            .then((data) => setDocuments(data.documents || []))
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [currentClientID]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const filteredDocs = useMemo(() => {
        return documents.filter(doc => {
            if (searchYear && String(doc.document_year) !== searchYear) return false;
            if (searchName && !doc.document_name.toLowerCase().includes(searchName.toLowerCase())) return false;
            if (searchType && !doc.document_type.toLowerCase().includes(searchType.toLowerCase())) return false;
            return true;
        });
    }, [documents, searchYear, searchName, searchType]);

    const handleDownload = async (id) => {
        setDownloadingId(id);
        try {
            const res = await fetch(`${SERVER_URL}/documents/${id}/download`, {
                headers: { 'Authorization': 'Bearer ' + Cookies.get('accessToken') },
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'Failed to generate download link');
            }
            const data = await res.json();
            window.open(data.url, '_blank');
        } catch (err) {
            alert(err.message);
        } finally {
            setDownloadingId(null);
        }
    };

    const openEditModal = (doc) => {
        setEditTarget(doc.id);
        setEditName(doc.document_name);
        setEditYear(doc.document_year);
        setEditType(doc.document_type);
        setEditError('');
        setEditModalOpen(true);
    };

    const closeEditModal = () => {
        setEditModalOpen(false);
        setEditTarget(null);
        setConfirmDelete(false);
        setDeleting(false);
    };

    const handleSaveEdit = async () => {
        if (!editName.trim() || !editYear || !editType.trim()) {
            setEditError('All fields are required.');
            return;
        }

        setSaving(true);
        setEditError('');

        try {
            const res = await fetch(`${SERVER_URL}/documents/${editTarget}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + Cookies.get('accessToken'),
                },
                body: JSON.stringify({
                    document_name: editName,
                    document_year: parseInt(editYear, 10),
                    document_type: editType
                }),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'Failed to update document');
            }

            const data = await res.json();

            // Update local state
            setDocuments(prev => prev.map(d => d.id === editTarget ? data.document : d));
            closeEditModal();
        } catch (err) {
            setEditError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) {
            setConfirmDelete(true);
            return;
        }

        setDeleting(true);
        setEditError('');

        try {
            const res = await fetch(`${SERVER_URL}/documents/${editTarget}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': 'Bearer ' + Cookies.get('accessToken'),
                },
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'Failed to delete document');
            }

            // Update local state
            setDocuments(prev => prev.filter(d => d.id !== editTarget));
            closeEditModal();
        } catch (err) {
            setEditError(err.message);
        } finally {
            setDeleting(false);
        }
    };

    if (!currentClientID) {
        return (
            <div className="doc-empty">
                <p>No Client Selected</p>
                <small>Select a client from the main Search tab to view their documents.</small>
            </div>
        );
    }

    return (
        <div className="doc-wrapper">
            <div className="doc-header">
                <div className="cr-header">
                    <div className="cr-header-top">
                        {currentClientName && <h2 className="cr-client-name">{currentClientName}'s Documents</h2>}
                    </div>
                </div>

                <div className="doc-filters">
                    <input
                        type="text"
                        placeholder="Search by name..."
                        className="doc-filter-input"
                        value={searchName}
                        onChange={e => setSearchName(e.target.value)}
                    />
                    <input
                        type="number"
                        placeholder="Year"
                        min={1900}
                        className="doc-filter-input year-input"
                        value={searchYear}
                        onChange={e => setSearchYear(e.target.value)}
                    />
                    <select
                        id="doc-filter-input-select"
                        className="doc-filter-input doc-filter-input-select"
                        value={searchType}
                        onChange={e => setSearchType(e.target.value)}
                    >
                        <option value="">All Types</option>
                        <option value="Tax Return">Tax Return</option>
                        <option value="T4">T4</option>
                        <option value="T5">T5</option>
                        <option value="Notice of Assessment">Notice of Assessment</option>
                        <option value="Other">Other</option>
                    </select>
                </div>

                <div className="ds-create-btn-container">
                    <button className="ds-modal-open-btn" onClick={openUploadModal}>Add Documents</button>
                </div>
            </div>

            {error && <div className="doc-error">{error}</div>}

            {loading ? (
                <div className="doc-state">Loading documents...</div>
            ) : filteredDocs.length === 0 ? (
                <div className="doc-state">No documents found.</div>
            ) : (
                <div className="doc-list">
                    {filteredDocs.map(doc => (
                        <div key={doc.id} className="doc-row">
                            <div className="doc-main">
                                <h3 className="doc-title">{doc.document_name}</h3>
                                <div className="doc-meta">
                                    <span className="doc-tag doc-tag--year">{doc.document_year}</span>
                                    <span className="doc-tag doc-tag--type">{doc.document_type}</span>
                                </div>
                            </div>
                            <div className="doc-actions">
                                <button className="doc-btn doc-btn--edit" onClick={() => openEditModal(doc)}>
                                    Edit
                                </button>
                                <button
                                    className="doc-btn doc-btn--download"
                                    onClick={() => handleDownload(doc.id)}
                                    disabled={downloadingId === doc.id}
                                >
                                    {downloadingId === doc.id ? 'Loading...' : 'Download'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editModalOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={closeEditModal}>
                    <div style={{ background: '#1a1a1a', padding: '2rem', borderRadius: '8px', width: '400px', border: '1px solid #333' }} onClick={e => e.stopPropagation()}>
                        <h2 style={{ margin: '0 0 1.5rem 0', color: '#fff', fontSize: '1.2rem' }}>Edit Document</h2>

                        {editError && <div style={{ color: '#e05252', marginBottom: '1rem', fontSize: '0.9rem' }}>{editError}</div>}

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#aaa', fontSize: '0.85rem' }}>Name</label>
                                <input className="doc-filter-input" style={{ width: '100%', boxSizing: 'border-box' }} value={editName} onChange={e => setEditName(e.target.value)} />
                            </div>
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#aaa', fontSize: '0.85rem' }}>Year</label>
                                    <input type="number" min={1900} className="doc-filter-input" style={{ width: '100%', boxSizing: 'border-box' }} value={editYear} onChange={e => setEditYear(e.target.value)} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#aaa', fontSize: '0.85rem' }}>Type</label>
                                    <select className="doc-filter-input doc-filter-input-select" style={{ width: '100%', boxSizing: 'border-box' }} value={editType} onChange={e => setEditType(e.target.value)}>
                                        <option value="Tax Return">Tax Return</option>
                                        <option value="T4">T4</option>
                                        <option value="T5">T5</option>
                                        <option value="Notice of Assessment">Notice of Assessment</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', marginTop: '2rem' }}>
                            <button
                                className={`doc-btn ${confirmDelete ? 'doc-btn--delete-confirm' : 'doc-btn--delete'}`}
                                onClick={handleDelete}
                                disabled={saving || deleting}
                            >
                                {deleting ? 'Deleting...' : confirmDelete ? 'Confirm Delete' : 'Delete'}
                            </button>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button className="doc-btn doc-btn--edit" onClick={closeEditModal} disabled={saving || deleting}>Cancel</button>
                                <button className="doc-btn doc-btn--download" onClick={handleSaveEdit} disabled={saving || deleting || confirmDelete}>
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {uploadModalOpen && (
                <div className="modal-overlay" onClick={closeUploadModal}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">Upload Documents</h2>
                            <button className="modal-close" onClick={closeUploadModal}>&#x2715;</button>
                        </div>
                        <div className="modal-body">
                            <UploadDocument currentClientID={currentClientID} closeUploadModal={closeUploadModal} setDocuments={setDocuments} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Search;
